import React, { ReactNode, useId, useState } from 'react';
import classNames from 'classnames';

import './Collapsible.scss';

interface CollapsibleProps {
  /** Header label. A plain string or a rich node. */
  title: ReactNode;
  /** Body revealed while expanded. Mounted only when expanded, so heavy children are deferred. */
  children: ReactNode;
  /**
   * Controls pinned to the far end of the header, outside the toggle button
   * (e.g. an info button). Not part of the clickable toggle target.
   */
  headerActions?: ReactNode;
  /** Uncontrolled initial expanded state. Ignored when `expanded` is provided. */
  defaultExpanded?: boolean;
  /** Controlled expanded state. Provide together with `onToggle`. */
  expanded?: boolean;
  /** Explicit id for the body region (drives `aria-controls`). Auto-generated when omitted. */
  id?: string;
  className?: string;
  /** Fired with the next expanded state whenever the toggle is activated. */
  onToggle?(expanded: boolean): void;
}

/**
 * Generic disclosure primitive: a tinted header (chevron + title + optional trailing
 * actions) over an animated-chevron collapsible body. Supports both controlled
 * (`expanded` + `onToggle`) and uncontrolled (`defaultExpanded`) usage.
 *
 * The body is mounted only while expanded, so consumers can defer rendering heavy
 * children until the section is opened.
 */
export const Collapsible = ({
  title,
  children,
  headerActions,
  defaultExpanded = false,
  expanded,
  onToggle,
  id,
  className
}: CollapsibleProps) => {
  const generatedId = useId();
  const bodyId = id ?? `collapsible-${generatedId}`;

  const isControlled = expanded !== undefined;
  const [internalExpanded, setInternalExpanded] = useState(defaultExpanded);
  const isExpanded = isControlled ? expanded : internalExpanded;

  const handleToggle = () => {
    const next = !isExpanded;

    if (!isControlled) {
      setInternalExpanded(next);
    }

    onToggle?.(next);
  };

  return (
    <div
      className={className}
      styleName={classNames('collapsible', { 'collapsible--expanded': isExpanded })}
    >
      <div styleName="collapsible__header">
        <button
          type="button"
          styleName="collapsible__toggle"
          aria-expanded={isExpanded}
          aria-controls={bodyId}
          onClick={handleToggle}
        >
          <span
            className="dashicon dashicon-chevron-right"
            styleName="collapsible__chevron"
            aria-hidden="true"
          />
          <span styleName="collapsible__title">{title}</span>
        </button>
        {headerActions && <div styleName="collapsible__actions">{headerActions}</div>}
      </div>

      {isExpanded && (
        <div id={bodyId} styleName="collapsible__body">
          {children}
        </div>
      )}
    </div>
  );
};

export default Collapsible;
